import React, { useMemo } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

import {
  formatSchoolType,
  SCHOOL_BOARD_FIELDS,
  SCHOOL_STATUS,
  SCHOOL_STATUS_LABELS,
  VIEW_DASH,
} from "constants/schoolConstants";
import {
  getAgentByUamId,
  getAgentLabelByIdentifierField,
  getBoardDisplayState,
  getSchoolAgentUserIds,
  useSchoolDetailsLookups,
} from "helpers/schoolUtils";
import { useFetchAgentsByUserIds } from "store/backend/apiHooks";
import { Section, FieldGrid, CopyIdField, ChipList, Field } from "shared/components/components";

import layoutStyles from "../ViewBranchDetails/ViewBranchDetails.module.scss";
import styles from "./ViewSchoolDetails.module.scss";

const ViewSchoolDetails = ({
  schoolDetails,
  fees,
  category,
  isLoadingFeeAndCategory = false,
}) => {
  const {
    lookups,
    isBoardListLoading,
    isGradeListLoading,
    isCountryListLoading,
    isCityListLoading,
  } = useSchoolDetailsLookups(schoolDetails);
  const agentUserIds = useMemo(
    () => getSchoolAgentUserIds(schoolDetails),
    [schoolDetails],
  );
  const { data: agentsResponse, isFetching: isFetchingAgents } =
    useFetchAgentsByUserIds(agentUserIds);
  if (!schoolDetails) return null;
  const boardSections = SCHOOL_BOARD_FIELDS.map(({ name, label }) => ({
    label,
    ...getBoardDisplayState(
      schoolDetails[name] ?? [],
      lookups.boards,
      isBoardListLoading,
    ),
  }));
  const pocAgent = getAgentByUamId(agentsResponse, schoolDetails.pocId);
  const executiveAgent = getAgentByUamId(agentsResponse,schoolDetails.executiveId);
  const gradeWiseRows = schoolDetails.gradeWiseStudentCount || [];
  return (
    <div className={layoutStyles.pageCard}>
      <header className={layoutStyles.hero}>
        <div className={layoutStyles.heroMain}>
          <div className={layoutStyles.logoMark} aria-hidden>
            <SchoolOutlinedIcon className={layoutStyles.logoMarkIcon} />
          </div>
          <div className={layoutStyles.heroText}>
            <h2 className={layoutStyles.heroTitle}>{schoolDetails.schoolName}</h2>
            <div className={layoutStyles.heroMeta}>
              {schoolDetails.status ? (
                <span
                  className={`${styles.statusBadge} ${
                    schoolDetails.status === SCHOOL_STATUS.ACTIVE
                      ? styles.statusBadgeActive
                      : styles.statusBadgeInactive
                  }`}
                >
                  {SCHOOL_STATUS_LABELS[schoolDetails.status] || VIEW_DASH}
                </span>
              ) : null}
              <span className={styles.typeBadge}>
                {formatSchoolType(schoolDetails.schoolType) || VIEW_DASH}
              </span>
            </div>
          </div>
        </div>
      </header>
      <Section icon={InfoOutlinedIcon} title="School information">
        <FieldGrid>
          <CopyIdField
            label="School code"
            idValue={schoolDetails.schoolCode}
            ariaLabel="Copy school code to clipboard"
          />
          <Field label="UDISE code" value={schoolDetails.udiseCode} />
          <Field
            label="School POC"
            value={getAgentLabelByIdentifierField(
              pocAgent,
              "phone",
            )}
            isLoading={isFetchingAgents && !!schoolDetails.pocId}
          />
          <Field
            label="Genius Execution Rep"
            value={getAgentLabelByIdentifierField(
              executiveAgent,
              "employeeCode",
            )}
            isLoading={isFetchingAgents && !!schoolDetails.executiveId}
          />
          <Field
            label="Fees"
            value={fees}
            isLoading={isLoadingFeeAndCategory}
          />
          <Field
            label="Category"
            value={category}
            isLoading={isLoadingFeeAndCategory}
          />
          <Field
            label="Tier"
            value={lookups.tier?.value}
            isLoading={lookups.tier?.isLoading}
          />
          {boardSections.map(({ label, labels, isLoading }) => (
            <ChipList
              key={label}
              label={label}
              labels={labels}
              isLoading={isLoading}
            />
          ))}
        </FieldGrid>
      </Section>
      <Section icon={LocationOnOutlinedIcon} title="Address">
        <FieldGrid>
          <Field label="Address" fullWidth value={schoolDetails.address} />
          <Field label="Area" value={schoolDetails.area} />
          <Field
            label="Country"
            value={lookups.country?.value}
            isLoading={lookups.country?.isLoading || isCountryListLoading}
          />
          <Field
            label="City"
            value={lookups.city?.value}
            isLoading={lookups.city?.isLoading || isCityListLoading}
          />
          <Field
            label="State"
            value={lookups.state?.value}
            isLoading={lookups.state?.isLoading}
          />
          <Field
            label="District"
            value={lookups.district?.value}
            isLoading={lookups.district?.isLoading}
          />
          <Field label="Postal code" value={schoolDetails.postalCode} />
        </FieldGrid>
      </Section>
      <Section icon={GroupsOutlinedIcon} title="Enrollment">
        <FieldGrid>
          <Field label="Total students" value={schoolDetails.totalStudentCount} />
          <Field label="Total teachers" value={schoolDetails.totalTeacherCount} />
        </FieldGrid>
      </Section>
      <Section icon={SchoolOutlinedIcon} title="Grade-wise student count">
        {gradeWiseRows.length ? (
          <div className={styles.gradeChipPanel}>
            <div className={styles.gradeChipGrid}>
              {gradeWiseRows.map((row) => (
                <div key={row.gradeId} className={styles.gradeChip}>
                  <span className={styles.gradeChipLabel}>
                    {isGradeListLoading || lookups.grades[row.gradeId]?.isLoading
                      ? "Loading…"
                      : (lookups.grades[row.gradeId]?.value ?? VIEW_DASH)}
                  </span>
                  <span className={styles.gradeChipCount}>{row.studentCount}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className={styles.emptyNote}>No grade-wise data available.</p>
        )}
      </Section>
    </div>
  );
};

export default ViewSchoolDetails;
